// apps/mcv-client/src/components/Layout/SideNav/ProviderSection.tsx
import { useUser } from '@clerk/react-router';
import { CheckCircleIcon, StarIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { Provider } from '~/types';

const TAGS = CT.layout.navDrawer;

const getInitials = (provider: Provider | undefined): string => {
  if (!provider) return '?';
  const first = provider.firstName?.[0] || '';
  const last = provider.lastName?.[0] || '';
  return (first + last).toUpperCase() || '?';
};

export const ProfileSection = ({ provider }: { provider: Provider | undefined }) => {
  const { user } = useUser();

  if (!provider) return null;

  const displayName = [provider.prefixName, provider.firstName, provider.lastName]
    .filter(Boolean)
    .join(' ');
  const suffix = provider.suffixName ? `, ${provider.suffixName}` : '';
  const typeCode = provider.type?.providerTypeCode || '';
  const nameLine = `${displayName}${suffix}`;

  const verificationLevel = provider.verificationLevel || 0;

  return (
    <div className="px-3 pt-4 pb-3 border-b border-primary-t-700" data-cy={TAGS.layout}>
      <div className="rounded-lg bg-white/[0.06] px-3 py-4">
        <div className="flex flex-col items-center text-center gap-2.5">
          {user?.hasImage ? (
            <img
              src={user.imageUrl}
              alt={displayName}
              className="w-[52px] h-[52px] rounded-full object-cover ring-2 ring-white/20 shadow-lg"
            />
          ) : (
            <div className="w-[52px] h-[52px] rounded-full bg-gradient-to-br from-primary-50 to-primary-300 flex items-center justify-center text-white text-base font-semibold ring-2 ring-white/20 shadow-lg">
              {getInitials(provider)}
            </div>
          )}
          <div className="min-w-0 w-full">
            <p className="text-base font-semibold text-grey-100 truncate leading-snug">
              {nameLine}
            </p>
            {typeCode && (
              <p className="text-base font-semibold text-grey-100 truncate leading-snug mt-0.5">
                {typeCode}
              </p>
            )}
            {provider.npiNum && (
              <p className="text-[13px] font-medium text-grey-400 mt-1">NPI {provider.npiNum}</p>
            )}
            {provider.type?.description && (
              <p className="text-[13px] text-grey-400 break-words mt-1.5">
                {provider.type.description}
              </p>
            )}
          </div>
        </div>

        {/* Three verification checkmarks — each lights up green once the provider's
            verificationLevel reaches that index (1, 2, 3). A gold star sits at the end,
            always rendered but greyed-out until fully verified (level 4+). */}
        {(() => {
          const labelFor = (n: number) =>
            verificationLevel >= n ? `Level ${n} reached` : `Level ${n} not reached`;
          const level1Label = labelFor(1);
          const level2Label = labelFor(2);
          const level3Label = labelFor(3);
          const fullyVerified = verificationLevel >= 4;
          const starLabel = fullyVerified ? 'Fully verified' : 'Not yet fully verified';
          const a11yLabel = `${level1Label}, ${level2Label}, ${level3Label}, ${starLabel}`;
          return (
            <div
              role="img"
              aria-label={a11yLabel}
              className="flex justify-center items-center gap-1.5 mt-3 pt-2.5 border-t border-white/[0.06]"
            >
              <CheckCircleIcon
                className={`h-7 w-7 ${verificationLevel >= 1 ? 'text-good' : 'text-grey-800'}`}
                title={level1Label}
                aria-hidden="true"
              />
              <CheckCircleIcon
                className={`h-7 w-7 ${verificationLevel >= 2 ? 'text-good' : 'text-grey-800'}`}
                title={level2Label}
                aria-hidden="true"
              />
              <CheckCircleIcon
                className={`h-7 w-7 ${verificationLevel >= 3 ? 'text-good' : 'text-grey-800'}`}
                title={level3Label}
                aria-hidden="true"
              />
              <div
                className={`flex items-center justify-center h-7 w-7 rounded-full ml-0.5 ${
                  fullyVerified ? 'bg-gold' : 'bg-gold/20'
                }`}
                title={starLabel}
                aria-hidden="true"
              >
                <StarIcon className={`h-4 w-4 ${fullyVerified ? 'text-white' : 'text-white/40'}`} />
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};
